import { FlexBox, Loader } from '@airship-platform/jam-web/components';
import {
  ProposalTemplateOptionsFilterForm,
  ProposalTemplateOptionsFilterFormInputs,
} from '@airship-platform/airship-proposal';
import Alert from '@mui/material/Alert';
import { useMemo, useState } from 'react';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import { useProposalTemplates } from '../../../data-access/proposalTemplate';
import ProposalTemplatesQueryResults from './ProposalTemplatesQueryResults';

const ProposalTemplatesQuery: React.FC = () => {
  const {
    proposalTemplates,
    isLoadedTemplates,
    isLoadingTemplates,
    tags,
    isLoadedTags,
    isLoadingTags,
  } = useProposalTemplates();
  const { currentProposal } = useAppointmentProposals();
  const [filterValues, setFilterValues] =
    useState<ProposalTemplateOptionsFilterFormInputs>({
      searchTerm: '',
      tags: [],
    });

  const filteredTemplates = useMemo(
    () =>
      proposalTemplates.filter(
        (template) =>
          [
            template.name,
            template.summary,
            template.options
              .flatMap((d) => d.products)
              .flatMap((product) => [product.name, product.model]),
          ]
            .flat()
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(filterValues.searchTerm.toLowerCase()) &&
          (!filterValues.tags.length ||
            filterValues.tags.every((tagId) =>
              [template.tagIds, template.options.flatMap((o) => o.tagIds)]
                .flat()
                .some((t) => t === tagId)
            ))
      ),
    [filterValues, proposalTemplates]
  );

  const isLoading = [isLoadingTemplates, isLoadingTags].some(Boolean);
  const isLoaded = [isLoadedTemplates, isLoadedTags].every(Boolean);

  if ((isLoading && !isLoaded) || !currentProposal) {
    return (
      <Loader isLoading={isLoading} message="Loading proposal templates..." />
    );
  }

  if (!proposalTemplates.length) {
    return (
      <Alert severity="info" variant="outlined" icon={false}>
        No templates yet. Ask your manager or admin to create some to save time
        and boost close rates.
      </Alert>
    );
  }

  return (
    <FlexBox flexDirection={'column'} gap={3} width={'100%'}>
      <ProposalTemplateOptionsFilterForm
        proposalTemplateTags={tags}
        onChange={setFilterValues}
      />

      <ProposalTemplatesQueryResults
        proposalTemplateTags={tags}
        proposalTemplates={filteredTemplates}
      />
    </FlexBox>
  );
};

export default ProposalTemplatesQuery;
